import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '@env';
import { ApiGetSmsListHistoryOptions, filterKey } from '@models/reseller/sms-history-detail';
import { ApiGetSubaccounts, Subaccount } from '@models/subaccount/subaccount';
import { User } from '@models/user';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { SmsHistoryService } from '@services/sms-history.service';
import { SmsService } from '@services/sms.service';
import { SubaccountService } from '@services/subaccount.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { SmsDetailRenderComponent } from '@ui/components/atlas-grid/renderer/sms-detail-render/sms-detail-render.component';
import { creditsTypeIcon } from '@utils/constants';
import { formateDateTimeWithSeconds } from '@utils/utils';
import { GridReadyEvent } from 'ag-grid-community';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { HistoryDetailModalComponent } from 'src/app/views/reseller/reseller-history/history-modal-detail/history-detail-modal.component';
import { LoaderService } from '@services/loader.service';

@Component({
  selector: 'wsb-sms-sent',
  templateUrl: './sms-sent.component.html',
  styleUrls: ['./sms-sent.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class SmsSentComponent implements OnInit, AfterViewInit {
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalExportSmsList') modalExportSmsList;
  @ViewChild('modalSuccessExport') modalSuccessExport;
  @ViewChild('modalExportError') modalExportError;

  @Input() user: User;

  modalSuccessExportModal: NgbModalRef;
  modalExportErrorModal: NgbModalRef;
  modalExportSmsListModal: NgbModalRef;

  fc: UntypedFormControl = new UntypedFormControl('XLS', Validators.required);

  apiGetSmsListHistoryOptions = new ApiGetSmsListHistoryOptions();

  aggregableByCampain = false;

  private search: Subject<string> = new Subject();
  gridApi;
  gridColumnApi;
  tableLoading: boolean;
  tableHistoryGridOptions = null;
  tableColumnsDefs: any = [];
  paginationPageSize = 10;
  tableHistoryData;
  components = {
    smsDetailRederer: SmsDetailRenderComponent,
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  scrollPageSubs = 1;
  subaccounts: Array<Subaccount> = [];

  filter;
  today = new Date();

  dateFrom;
  dateTo;
  credits: any[];
  typeLabel;
  idMessage;

  labels = [
    { id: 'SENDER', label: 'common_sender' },
    { id: 'SENDING-METHOD', label: 'sms_detail_sentfrom' },
    { id: 'RICH-SMS', label: 'sms_detail_sentfrom' }
  ];
  gridAnimationState = 'hide';
  constructor(
    private smsHistoryService: SmsHistoryService,
    private smsService: SmsService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private router: Router,
    private subaccountService: SubaccountService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.translateLables();

    this.getCreditsType();

    this.defineTable();

    this.gridOptionDefinition();

    //
  }

  ngAfterViewInit(): void {
    this.grid.dataIsLoaded.subscribe(() => {
      this.gridAnimationState = 'show';
    });
    this.grid.exportGlobal.subscribe(aggregableByCampain => {
      if (this.checkDateIntervalForExport()) {
        return this.openModalExportError();
      }
      this.aggregableByCampain = aggregableByCampain;
      this.exportCampaing();
    });
  }

  translateLables() {
    this.labels.forEach(label => {
      this.translateService.get(label.label).subscribe(translatedLabel => (label.label = translatedLabel));
    });
  }

  getCreditsType() {
    this.smsService.getMessageTypes().subscribe(data => {
      this.credits = data.data.map(x => {
        return {
          value: x.id,
          icon: creditsTypeIcon[x.id],
          title: x.label
        };
      });
    });
  }

  getSubaccountList() {
    this.search.pipe(debounceTime(250)).subscribe(filter => {
      const body: ApiGetSubaccounts = {
        login: filter
      } as ApiGetSubaccounts;

      this.subaccountService.getSubaccounts(null, null, null, null, body).subscribe(subs => {
        this.subaccounts = subs.data;
      });
    });
  }

  onSearch($event) {
    this.search.next($event.term);
  }

  defineTable() {
    this.tableColumnsDefs = [
      {
        headerNameTranslate: 'common_title',
        field: 'campaignName',
        colPrefKey: 'campaignName',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return (
                '<div class="d-flex title"> <div class="overflow-h"> <div class="campaignName title-overflow color-primary">' +
                params.data.campaignName +
                '</div> </div> <span > ' +
                this.labels[0].label +
                ' ' +
                params.data.sender +
                '</span> <span > ' +
                this.labels[1].label +
                ': ' +
                params.data.sentFrom +
                '</span> </div>'
              );
            } else {
              return '';
            }
          }
        },

        sortable: false,
        onCellClicked: params => {
          this.openHistoryDetailModal(params.data.idMessage, params.data.isRichsms);
        },
        width: 180
      },

      {
        headerNameTranslate: 'common_text',
        field: 'message',
        colPrefKey: 'message',
        cellRenderer: 'htmlRenderer',
        width: 300,
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const label = this.translateService.instant('encrypted_label');
              return (
                '<div style="overflow: hidden;"> <div class="message-overflow">' +
                (params.data.message === 'xxx' ? label : params.data.message) +
                '</div></div>'
              );
            } else {
              return '';
            }
          }
        },
        sortable: false
      },
      {
        headerNameTranslate: 'common_status',
        field: 'status',
        sortable: false,
        colPrefKey: 'status',
        suppressCellFocus: true,
        width: 125,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              let badgeLabel;
              this.translateService.get('sms_status_sent').subscribe(label => {
                badgeLabel = label;
              });
              return '<span class="badge badge-delivered">' + badgeLabel + '</span>';
            } else {
              return '';
            }
          }
        }
      },
      {
        headerNameTranslate: 'common_number_recipient',
        colPrefKey: 'recipients',
        field: 'recipients',
        suppressCellFocus: true,
        cellRenderer: 'smsDetailRederer',
        sortable: false,
        width: 120
      },
      {
        headerNameTranslate: 'common_data_added',
        field: 'createTime',
        colPrefKey: 'createTime',
        sortable: true,
        width: 110,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const dateTime = formateDateTimeWithSeconds(params.data.createTime).split(' ');
              const date = dateTime[0];
              const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
              return '<div class="dateTime"><span>' + date + '</span> <span>' + time + '</span></div>';
            }
            return '';
          }
        }
      },
      {
        headerNameTranslate: 'common_data_sent',
        field: 'dateForSend',
        colPrefKey: 'dateForSend',
        cellRenderer: 'htmlRenderer',
        sortable: true,
        sort: 'desc',
        width: 110,
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const dateTime = formateDateTimeWithSeconds(params.data.dateForSend).split(' ');
              const date = dateTime[0];
              const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
              return '<div class="dateTime"><span>' + date + '</span> <span>' + time + '</span></div>';
            }
            return '';
          }
        }
      },
      {
        headerNameTranslate: 'common_type',
        field: 'idMessageType',
        colPrefKey: 'idMessageType',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const icon = this.credits.filter(type => type.value === params.data.idMessageType);
              if (params.data.isRichsms) {
                return this.richSmsHtml(icon);
              }
              return '<div style="align-items: center;"> <img class="ml-2 info-icon" src="' + icon[0]?.icon + '" alt="" />';
            } else {
              return '';
            }
          }
        },
        width: 110,
        sortable: false
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: e => this.openHistoryDetailModal(e.idMessage, e.isRichsms),
              label: 'sms_campaigns_detail'
            },
            {
              onClick: e => {
                if (this.checkDateIntervalForExport()) {
                  return this.openModalExportError();
                }
                this.idMessage = e.idMessage;
                this.openExportModal(e.idMessage);
              },
              label: 'sms_recent_detail_export'
            },
            {
              onClick: e => this.router.navigate(['/stats/richsms-detail'], { queryParams: { uuid: e.idMessage } }),
              label: 'sms_campaigns_richsms_stats_action',
              hide: e => !e?.isRichsms
            }
          ]
        },

        cellClass: ['button-group', 'text-end'],
        maxWidth: 82,
        field: '__actions'
      }
    ];
  }

  gridOptionDefinition() {
    const that = this;
    const key = 'period';

    this.tableHistoryGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.filter = this.grid.filtersModel[key];
        this.loadData();
      },
      onFilterChange: (event: any) => {
        if ((event.label as string)?.includes('timefilter')) {
          this.onFilterChange(event.value);
          this.grid.filter();
        } else {
          this.buildDate();
        }
      },
      rowHeight: 110,
      pagination: true,
      columnCustomizationKey: 'contacts-list',
      export: 'export',
      exportText: 'reseller_history_export_time_help',
      exportButtons: [
        { label: 'export_sms', value: 'false' },
        { label: 'campagin_sms_export', value: 'true' }
      ],
      paginationPageSize: this.paginationPageSize,
      filters: this.getFilters(),
      loadFunction: this.reloadList.bind(that),

      noRowsOverlayOptions: {
        message: ''
      }
    });
  }

  loadData() {
    this.grid.filtersModel[filterKey.period] = {
      label: 'timefilter_dates_thismonth',
      value: 'thismonth',
      translate: true
    };
    this.onFilterChange('thismonth');
    this.grid.refresh();
  }

  reloadList(startRow?, endRow?, sort?) {
    this.loaderService.show();
    return this.smsHistoryService
      .getSmsSentList(
        this.apiGetSmsListHistoryOptions.dateFrom,
        this.apiGetSmsListHistoryOptions.dateTo,
        this.apiGetSmsListHistoryOptions.recipient,
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? sort[0].colId : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
      )
      .pipe(finalize(() => this.loaderService.hide()));
  }

  richSmsHtml(icon) {
    let labelSmsRich;
    this.translateService.get('common_sms_link').subscribe(label => {
      labelSmsRich = label;
      if (environment.brandName === 'smsenvoi') {
        labelSmsRich = 'RICH SMS';
      }
    });

    return (
      '<div class="d-flex flex-direction-c"> <div style="align-items: center;">' +
      '<img " class="ml-2 info-icon" src="' +
      icon[0]?.icon +
      '" alt="" /> </div> <span class="badge badge-rich">' +
      labelSmsRich +
      '</span>  </div>'
    );
  }

  buildDate() {
    const offset = moment(this.today).utcOffset();
    let timezone;
    if (offset < 0) {
      timezone =
        '-' +
        this.today
          .toString()
          .split('-')[1]
          .split(' ')[0];
    } else {
      timezone =
        '+' +
        this.today
          .toString()
          .split('+')[1]
          .split(' ')[0];
    }

    const dateFrom =
      moment(this.grid.filtersModel[filterKey.fromDateKey])
        .format()
        .split('T')[0] +
      'T' +
      this.grid.filtersModel[filterKey.fromTimeKey] +
      ':00' +
      timezone;
    const dateTo =
      moment(this.grid.filtersModel[filterKey.toDateKey])
        .format()
        .split('T')[0] +
      'T' +
      this.grid.filtersModel[filterKey.toTimeKey] +
      ':00' +
      timezone;
    this.apiGetSmsListHistoryOptions.dateFrom = dateFrom;
    this.apiGetSmsListHistoryOptions.dateTo = dateTo;
    this.apiGetSmsListHistoryOptions.recipient = this.grid.filtersModel[filterKey.recipient]
      ? this.grid.filtersModel[filterKey.recipient]
      : null;
  }

  getFilters() {
    return [
      {
        name: 'recipient',
        label: 'timefilter_mobile',
        type: 'text'
      },
      {
        name: 'period',
        label: 'common_from',
        type: 'dropdown',
        options: [
          {
            label: 'timefilter_dates_last6months',
            value: 'all',
            translate: true
          },
          {
            label: 'timefilter_dates_lasthour',
            value: 'lastHour',
            translate: true
          },
          {
            label: 'timefilter_dates_last24hours',
            value: 'last24hours',
            translate: true
          },
          {
            label: 'timefilter_dates_today',
            value: 'today',
            translate: true
          },
          {
            label: 'timefilter_dates_thisweek',
            value: 'thisweek',
            translate: true
          },
          {
            label: 'timefilter_dates_thismonth',
            value: 'thismonth',
            translate: true
          },
          {
            label: 'timefilter_dates_lastmonth',
            value: 'lastmonth',
            translate: true
          }
        ]
      },
      {
        name: 'fromDate',
        label: 'common_from',
        type: 'date'
      },
      {
        name: 'fromTime',
        label: 'common_from',
        type: 'time'
      },
      {
        name: 'toDate',
        label: 'common_to',
        type: 'date'
      },
      {
        name: 'toTime',
        label: 'common_from',
        type: 'time'
      }
    ];
  }

  checkDateIntervalForExport() {
    const dateFrom = new Date(this.grid.filtersModel[filterKey.fromDateKey]);
    const dateTo = new Date(this.grid.filtersModel[filterKey.toDateKey]);
    const diff = Math.round((dateFrom.getTime() - dateTo.getTime()) / (1000 * 60 * 60 * 24));
    return Math.abs(diff) > 31;
  }

  getApiGetSmsListOptions() {
    const apiGetSmsListHistoryOptions = new ApiGetSmsListHistoryOptions();
    const offset = moment(this.today).utcOffset();
    let timezone;
    if (offset < 0) {
      timezone =
        '-' +
        this.today
          .toString()
          .split('-')[1]
          .split(' ')[0];
    } else {
      timezone =
        '+' +
        this.today
          .toString()
          .split('+')[1]
          .split(' ')[0];
    }

    const dateFrom = moment(this.grid.filtersModel[filterKey.fromDateKey]).format();
    const dateTo = this.grid.filtersModel[filterKey.toDateKey] + 'T' + this.grid.filtersModel[filterKey.toTimeKey] + +':00' + timezone;
    const recipient = this.grid.filtersModel[filterKey.recipient] ? this.grid.filtersModel[filterKey.recipient] : null;
    apiGetSmsListHistoryOptions.dateFrom = dateFrom;
    apiGetSmsListHistoryOptions.dateTo = dateTo;
    apiGetSmsListHistoryOptions.recipient = recipient;
    return apiGetSmsListHistoryOptions;
  }

  onFilterChange(filter) {
    let dateFrom;
    let dateTo;
    let timezone;
    const offset = moment(this.today).utcOffset();
    if (offset < 0) {
      timezone =
        '-' +
        this.today
          .toString()
          .split('-')[1]
          .split(' ')[0];
    } else {
      timezone =
        '+' +
        this.today
          .toString()
          .split('+')[1]
          .split(' ')[0];
    }

    switch (filter) {
      case 'all':
        dateFrom = new Date(null);

        break;
      case 'lastHour':
        dateFrom = moment()
          .subtract(1, 'hour')
          .format();

        break;

      case 'last24hours':
        dateFrom = moment()
          .subtract(24, 'hour')
          .format();

        break;

      case 'today':
        dateFrom = moment()
          .startOf('day')
          .format();

        break;

      case 'thisweek':
        dateFrom = moment()
          .startOf('isoWeek')
          .format();

        break;

      case 'thismonth':
        dateFrom = moment()
          .startOf('month')
          .format();

        break;

      case 'lastmonth':
        dateFrom = moment()
          .startOf('month')
          .subtract(1, 'month')
          .format();
        dateTo = moment()
          .startOf('month')
          .format();
        break;
      default:
        break;
    }

    const newDate = new Date(
      moment(dateFrom)
        .format('ddd MMM DD YYYY HH:mm:ss')
        .toString()
    );

    this.apiGetSmsListHistoryOptions.dateFrom =
      moment(dateFrom)
        .format('YYYY-MM-DDTHH:mm:ss')
        .toString() +
      '+' +
      timezone;
    this.apiGetSmsListHistoryOptions.dateTo =
      moment()
        .format('YYYY-MM-DDTHH:mm:ss')
        .toString() +
      '+' +
      timezone;

    this.setFilters(newDate, dateFrom, dateTo);
  }

  private setFilters(newDate: Date, dateFrom: any, filterDateTo) {
    let dateTo;
    if (filterDateTo) {
      dateTo = new Date(
        moment(filterDateTo)
          .format('ddd MMM DD YYYY HH:mm:ss')
          .toString()
      );
    }
    const toTime = dateTo ? moment(dateTo).format('HH:mm') : moment(this.today).format('HH:mm');
    this.grid.filtersModel[filterKey.fromDateKey] = newDate;
    this.grid.filtersModel[filterKey.fromTimeKey] = moment(dateFrom).format('HH:mm');
    this.grid.filtersModel[filterKey.toDateKey] = dateTo ? dateTo : this.today;
    this.grid.filtersModel[filterKey.toTimeKey] = toTime;
    this.buildDate();
  }

  openHistoryDetailModal(idMessage, isRichsms) {
    const options: NgbModalOptions = {
      size: 'xl',
      windowClass: 'custom-history-detail-modal-height'
    };
    const landingPageModal = this.modalService.open(HistoryDetailModalComponent, options);
    (landingPageModal.componentInstance as HistoryDetailModalComponent).user = this.user;
    (landingPageModal.componentInstance as HistoryDetailModalComponent).idMessage = idMessage;
    (landingPageModal.componentInstance as HistoryDetailModalComponent).isRichsms = isRichsms;
  }

  exportCampaing() {
    this.smsHistoryService
      .exportSendSmsList(this.fc.value, this.apiGetSmsListHistoryOptions, this.aggregableByCampain, this.user.profile.email)
      .subscribe(success => {
        this.modalService.dismissAll();
        this.openModalSuccessExport();
      });
  }

  openExportModal(isAggregateByCampaign?): void {
    this.aggregableByCampain = isAggregateByCampaign ? isAggregateByCampaign : false;
    this.modalExportSmsListModal = this.modalService.open(this.modalExportSmsList);
    this.modalExportSmsListModal.result.then(
      result => {},
      reason => {}
    );
  }

  openModalSuccessExport(): void {
    this.modalSuccessExportModal = this.modalService.open(this.modalSuccessExport);
    this.modalSuccessExportModal.result.then(
      result => {
        this.modalService.dismissAll();
      },
      reason => {
        this.modalService.dismissAll();
      }
    );
  }

  openModalExportError(): void {
    this.modalExportErrorModal = this.modalService.open(this.modalExportError);
    this.modalExportErrorModal.result.then(
      result => {
        this.modalService.dismissAll();
      },
      reason => {
        this.modalService.dismissAll();
      }
    );
  }

  exportSingleCampaign() {
    this.smsHistoryService.exportSendSmsDetail(this.fc.value, this.idMessage, this.user.profile.email).subscribe(() => {
      this.idMessage = null;
      this.modalService.dismissAll();
      this.openModalSuccessExport();
    });
  }
}
